import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SearchBar } from '@/components/SearchBar';
import { getCountryByValue, CountryRegions } from '@/data/regions';
import { PageHero } from '@/components/PageHero';
import { RegionsSelector } from '@/components/RegionsSelector';
import { DogSchoolsList } from '@/components/DogSchoolsList';
import { CountryRegionsLinks } from '@/components/CountryRegionsLinks';
import { EducationalSection } from '@/components/EducationalSection';
import { TableOfContents } from '@/components/TableOfContents';
import { fetchPaginatedDogSchools } from '@/services/paginated/paginatedService';
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Home, ChevronUp } from "lucide-react";
import { useSchoolSearch } from '@/hooks/useSchoolSearch';
import { ScrollToTop } from '@/components/ScrollToTop';
import { EnhancedSEOHead } from '@/components/SEO/EnhancedSEOHead';
import { BreadcrumbSchema } from '@/components/SEO/BreadcrumbSchema';

const INITIAL_PAGE_SIZE = 25;

const Country = () => {
  const { countryId } = useParams<{ countryId: string }>();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [countryData, setCountryData] = useState<CountryRegions | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  
  const {
    filteredSchools,
    isLoading,
    isSearching,
    error,
    userPostalCode,
    searchLocation,
    schoolCount,
    handleSearch,
    handleCountUpdate,
    isPaginated,
    hasMore,
    isLoadingMore,
    loadMoreSchools,
    setFilteredSchools,
    setSchoolCount,
    setIsLoading,
    setError,
    setSearchLocation,
    setIsPaginated,
    setHasMore
  } = useSchoolSearch();

  useEffect(() => {
    const loadSchools = async () => {
      if (countryId) {
        setIsLoading(true);
        setError(null);
        
        const country = getCountryByValue(countryId);
        setCountryData(country);
        
        setSelectedState(null);
        
        const countryName = countryId === 'germany' ? 'Germany' : 
                          countryId === 'austria' ? 'Austria' : 'Switzerland';
        
        setIsPaginated(true);
        
        try {
          console.log(`Loading schools for country: ${countryName} with pagination`);
          const result = await fetchPaginatedDogSchools(0, INITIAL_PAGE_SIZE, { country: countryName });
          
          console.log(`Loaded initial ${result.schools.length} schools of total: ${result.totalCount}`);
          
          if (result.schools.length === 0) {
            toast({
              title: t('noSchoolsFound'),
              description: t('tryDifferentSearch'),
              variant: "destructive",
            });
          } else {
            toast({
              title: t('schoolsFound'),
              description: `${result.schools.length} ${t('schoolsLoaded')} (${t('total')}: ${result.totalCount || '?'})`,
              variant: "default",
            });
          }
          
          setFilteredSchools(result.schools);
          setHasMore(result.hasMore);
          setSchoolCount(result.totalCount || result.schools.length);
          setSearchLocation('');
        } catch (error) {
          console.error("Error fetching dog schools:", error);
          setError("Failed to load dog schools. Please try again later.");
          setFilteredSchools([]);
          
          toast({
            title: t('error'),
            description: t('errorFetchingSchools'),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadSchools();
  }, [countryId, t, toast, setError, setFilteredSchools, setIsLoading, setSchoolCount, setSearchLocation, setIsPaginated, setHasMore]);

  const handleStateClick = (stateValue: string) => {
    navigate(`/${countryId}/${stateValue}`);
  };
  
  const countryName = countryData?.name[language === 'de' ? 'de' : 'en'] || '';
  const seoTitle = countryName && schoolCount !== undefined ? 
    `${schoolCount} Hundeschulen in ${countryName} | Hundetrainer & Welpenschule in der Nähe finden` : 
    'Hundeschulen in deiner Nähe | Hundeschulenfinder';
  const seoDescription = countryName && schoolCount !== undefined ?
    `Finde die beste von ${schoolCount} Hundeschulen in ${countryName}! ✓ Qualifizierte Hundetrainer ✓ Welpentraining ✓ Hundeerziehung ✓ Bewertungen vergleichen ✓ Jetzt die perfekte Hundeschule in deiner Nähe finden!` :
    'Finde die beste Hundeschule in deiner Nähe – schnell & unkompliziert! Vergleiche Angebote für Hundetraining, lies Bewertungen und wähle den besten Hundetrainer für deinen Vierbeiner.';
  const seoKeywords = `Hundeschule ${countryName}, Hundetrainer ${countryName}, Welpenschule ${countryName}, Hundeerziehung, Hundetraining in der Nähe, beste Hundeschule, Welpentraining, Hundeschulkindergarten, Hundeführerschein, Einzeltraining Hund`;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <EnhancedSEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`/${countryId}`}
        alternateLanguages={[
          { lang: 'de', url: `/${countryId}` },
          { lang: 'en', url: `/en/${countryId}` }
        ]}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Startseite', url: '/' },
          { name: countryName, url: `/${countryId}` }
        ]}
      />
      
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/">
            <Button variant="outline" className="flex items-center gap-2">
              <Home size={18} />
              {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
            </Button>
          </Link>
        </div>
        
        <PageHero 
          countryName={countryData?.name[language === 'de' ? 'de' : 'en']}
          backLink="/"
          backLinkText={t('allDogSchools')}
          schoolCount={schoolCount}
          searchLocation={searchLocation}
          countryId={countryId}
        />
        
        <section className="mb-8">
          <CountryRegionsLinks countryId={countryId} />
        </section>

        <section className="mb-8">
          <SearchBar onSearch={handleSearch} isSearching={isSearching} />
        </section>

        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
            {error}
          </div>
        )}

        <section className="mb-8">
          <TableOfContents schools={filteredSchools} defaultOpen={false} />
        </section>

        <section className="mb-16">
          <DogSchoolsList 
            schools={filteredSchools} 
            loading={isLoading} 
            userPostalCode={userPostalCode}
            onCountUpdate={handleCountUpdate}
            isPaginated={isPaginated}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMoreSchools}
          />
        </section>

        <section className="mb-16">
          <CountryRegionsLinks countryId={countryId} />
        </section>

        <section className="mb-16">
          <EducationalSection />
        </section>

        <section className="mb-16">
          <CountryRegionsLinks alwaysShowMainCountries={true} />
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Country;
